import { useState, useEffect } from 'react';
import { X, MessageCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { safeLocalStorage } from '@/lib/safeStorage';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { trackAdClick } from '@/lib/adTracking';

const BANNER_ID = '00000000-0000-0000-0000-000000000001';

const STORAGE_KEY = 'whatsapp-banner-dismissed';

interface BannerSettings {
  is_active: boolean;
  desktop_text: string;
  mobile_text: string;
  link_label: string;
  link_url: string;
}

export const WhatsAppBanner = () => {
  const { user, loading } = useAuth();
  const [dismissed, setDismissed] = useState(true); // Start hidden to avoid flash
  const [bannerSettings, setBannerSettings] = useState<BannerSettings | null>(null);

  useEffect(() => {
    const wasDismissed = safeLocalStorage.getItem(STORAGE_KEY) === 'true';
    setDismissed(wasDismissed);
  }, []);

  useEffect(() => {
    const fetchBannerSettings = async () => {
      const { data } = await supabase
        .from('banner_settings')
        .select('is_active, desktop_text, mobile_text, link_label, link_url')
        .eq('id', '00000000-0000-0000-0000-000000000001')
        .single();
      if (data) setBannerSettings(data as BannerSettings);
    };
    fetchBannerSettings();
  }, []);

  const handleDismiss = () => {
    safeLocalStorage.setItem(STORAGE_KEY, 'true');
    setDismissed(true);
  };

  // Don't show if loading, not logged in, dismissed, or banner is inactive
  if (loading || !user || dismissed || !bannerSettings || !bannerSettings.is_active) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-foreground py-2 px-4 relative z-40">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm pr-8">
        <MessageCircle className="h-4 w-4 flex-shrink-0 hidden sm:block" />
        
        {/* Desktop layout - horizontal */}
        <div className="hidden sm:flex sm:items-center sm:gap-2">
          <span className="truncate max-w-xl">{bannerSettings.desktop_text}</span>
          <a
            href={bannerSettings.link_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAdClick('banner', BANNER_ID)}
            className="font-semibold text-foreground underline underline-offset-2 hover:opacity-80 transition-opacity whitespace-nowrap"
          >
            {bannerSettings.link_label}
          </a>
        </div>
        
        {/* Mobile layout - stacked */}
        <div className="sm:hidden flex flex-col items-center gap-1 text-center">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 flex-shrink-0" />
            <span className="line-clamp-1">{bannerSettings.mobile_text}</span>
          </div>
          <a
            href={bannerSettings.link_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAdClick('banner', BANNER_ID)}
            className="font-semibold text-foreground underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            {bannerSettings.link_label}
          </a>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDismiss}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 text-foreground hover:bg-black/10 hover:text-foreground"
          aria-label="Dismiss banner"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
